import React, { FC } from 'react';
import styled from '@emotion/styled';
import {
  FontPrimaryRegular,
  spacingTopBottom,
  spacingCalculator,
  spacingAround,
  FontPrimarySemiBold,
  spacingTop, readablePrice, Icon,
} from '@digando/react-component-library';
import { mediaQueries } from '../../styles/media-queries';
import { useTranslation } from 'next-i18next';
import { GraduatedPrice } from '../../@types/codegen/graphql';

export interface ITierPriceList {
  key: number;
  duration: string;
  price: string;
  savings: string;
}

export interface ITierPriceListProps {
  prices: GraduatedPrice[];
}

const TierPriceOverviewComponent: FC<ITierPriceListProps> = (props): React.ReactElement => {
  const [t] = useTranslation();
  const { prices } = props;
  const maxPrice = Math.max(...prices.map(item => item.price));

  const tierPrices = props.prices
    .slice(0)
    .sort((priceA, priceB) => priceA.minDays - priceB.minDays)
    .map<ITierPriceList>((item) => {
      return {
        key: item.minDays,
        duration: t('product-page:days-of-rent', { count: (0 === item.minDays ? 1 : item.minDays) }),
        price: readablePrice(item.price),
        savings: Math.round(((item.price / maxPrice - 1) * -100)) + '%',
      };
    }) ?? [];

  return (
    <PriceTableWrapper>
      <TableWrapper>
        <Table>
          <thead>
            <tr>
              <TableRowHead>{t('duration')}</TableRowHead>
              <TableRowHead>{t('price') + ' / ' + t('day')}</TableRowHead>
              <TableRowHead>{t('savings')}</TableRowHead>
            </tr>
          </thead>

          <tbody>
            {tierPrices?.map((price, index) => (
              <tr key={index}>
                <TableRow>{price.duration}</TableRow>
                <TableRow>{price.price}</TableRow>
                <TableRow>{price.savings}</TableRow>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrapper>
      <SummaryWrapper><StyledIcon icon={'partner'} color={`var(--secondary-color)`}/></SummaryWrapper>
    </PriceTableWrapper>
  );
};

const Table = styled.table`
  width: 100%;

  thead {
    tr {
      border-bottom: 1px solid var(--border-light-color);
    }
  }

  tbody {
    tr:last-of-type {
      td {
        ${FontPrimarySemiBold};
        color: var(--secondary-color);
      }
    }
  }
`;

const TableRowHead = styled.td`
  ${FontPrimarySemiBold};
  font-size: var(--font-size-text);
  ${spacingTopBottom(1)};
  color: var(--black);

  &:last-of-type {
    text-align: right;

    @media ${mediaQueries.tablet} {
      text-align: center;
    }
  }
`;

const TableRow = styled.td`
  ${FontPrimaryRegular};
  font-size: var(--font-size-text);
  ${spacingTopBottom(1)};

  &:last-of-type {
    text-align: right;

    @media ${mediaQueries.tablet} {
      text-align: center;
    }
  }
`;

const PriceTableWrapper = styled.div`
  ${spacingAround(3)};
  background-color: var(--background-light);
  margin-bottom: ${spacingCalculator((4))};
  border-radius: var(--container-border-radius);

  @media ${mediaQueries.tablet} {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-rows: 1fr;
  }

  @media ${mediaQueries.laptop} {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }

  @media ${mediaQueries.laptopL} {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-rows: 1fr;
  }
`;

const TableWrapper = styled.div`
  ${FontPrimaryRegular};
  font-size: var(--font-size-text);

  @media ${mediaQueries.tablet} {
    grid-column: 1 / 3;
    grid-row: 1 / 2;
  }

  @media ${mediaQueries.laptopL} {
    grid-column: 1 / 3;
    grid-row: 1 / 2;

    td:nth-of-type(3) {
      text-align: center;
    }

    th:nth-of-type(3) {
      text-align: center;
    }
  }
`;

const SummaryWrapper = styled.div`
  display: flex;
  justify-content: center;
  color: var(--primary-color);
  ${spacingTop(4)};

  @media ${mediaQueries.tablet} {
    ${spacingTop(0)};
    align-self: center;
    grid-column: 3 / 3;
    grid-row: 1 / 2;
  }

  @media ${mediaQueries.laptop} {
    ${spacingTop(4)};
  }

  @media ${mediaQueries.laptopL} {
    ${spacingTop(0)};
  }
`;

const StyledIcon = styled(Icon)`
  width: 20% !important;
  height: auto !important;

  @media ${mediaQueries.tablet} {
    width: 80% !important;
  }

  @media ${mediaQueries.laptop} {
    width: 20% !important;
  }

  @media ${mediaQueries.laptopL} {
    width: 80% !important;
  }

`;

export const TierPriceOverview = React.memo(TierPriceOverviewComponent, (prevProps, nextProps) => {
  const prevKey = prevProps.prices?.map(price => price.price).join('');
  const nextKey = nextProps.prices?.map(price => price.price).join('');

  return prevKey === nextKey;
});
